import React, { useState } from 'react'

interface StatsPanelProps {
    title: string
    iframeSrc: string
}

const StatsPanel: React.FC<StatsPanelProps> = ({ title, iframeSrc }) => {
    const [isLoading, setIsLoading] = useState(true)
    const [hasError, setHasError] = useState(false)

    // Add authentication and display parameters to the iframe URL
    const enhancedSrc = `${iframeSrc}${iframeSrc.includes('?') ? '&' : '?'}theme=dark&embed=true&auth=public`

    // Handle iframe load events
    const handleIframeLoad = () => {
        setIsLoading(false)
    }

    const handleIframeError = () => {
        setIsLoading(false)
        setHasError(true)
    }

    return (
        <div className='flex h-full flex-col overflow-hidden rounded-lg bg-gray-800 shadow-md'>
            <h3 className='border-b border-gray-700 p-3 text-lg font-semibold text-white'>{title}</h3>
            <div className='relative flex-grow'>
                {isLoading && (
                    <div className='absolute inset-0 flex items-center justify-center bg-gray-800'>
                        <div className='h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-white'></div>
                    </div>
                )}

                {hasError && (
                    <div className='absolute inset-0 flex flex-col items-center justify-center bg-gray-800 p-4 text-center text-gray-400'>
                        <svg
                            xmlns='http://www.w3.org/2000/svg'
                            className='mb-2 h-12 w-12'
                            fill='none'
                            viewBox='0 0 24 24'
                            stroke='currentColor'
                        >
                            <path
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                strokeWidth={2}
                                d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
                            />
                        </svg>
                        <p>Unable to load statistics data</p>
                        <button
                            onClick={() => {
                                setIsLoading(true)
                                setHasError(false)
                                // Force iframe reload
                                const iframe = document.getElementById(`iframe-${title}`) as HTMLIFrameElement
                                if (iframe) {
                                    iframe.src = enhancedSrc
                                }
                            }}
                            className='mt-2 rounded bg-gray-700 px-3 py-1 text-sm text-white hover:bg-gray-600'
                        >
                            Retry
                        </button>
                    </div>
                )}

                <iframe
                    id={`iframe-${title}`}
                    src={enhancedSrc}
                    width='100%'
                    height='200'
                    frameBorder='0'
                    className='h-full w-full'
                    title={title}
                    onLoad={handleIframeLoad}
                    onError={handleIframeError}
                    sandbox='allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation'
                    allow='clipboard-write'
                    referrerPolicy='no-referrer'
                />
            </div>
        </div>
    )
}

const StatsPage = () => {
    const statsPanels = [
        {
            title: 'Volume',
            iframeSrc:
                'https://tristeroresearch.grafana.net/d-solo/aecp4egca639ce/mach-overview?var-time_int=1d&orgId=1&from=1747161493521&to=1747247893521&timezone=browser&panelId=2&__feature.dashboardSceneSolo&kiosk',
        },
        {
            title: 'Filled Trades',
            iframeSrc:
                'https://tristeroresearch.grafana.net/d-solo/aecp4egca639ce/mach-overview?var-time_int=1d&orgId=1&timezone=browser&panelId=5&__feature.dashboardSceneSolo&kiosk',
        },
        {
            title: 'All-Time-User-Retention',
            iframeSrc:
                'https://tristeroresearch.grafana.net/d-solo/aecp4egca639ce/mach-overview?var-time_int=1d&orgId=1&timezone=browser&panelId=11&__feature.dashboardSceneSolo&kiosk',
        },
        {
            title: 'DAU',
            iframeSrc:
                'https://tristeroresearch.grafana.net/d-solo/aecp4egca639ce/mach-overview?var-time_int=1d&orgId=1&from=1747161493521&to=1747247893521&timezone=browser&panelId=4&__feature.dashboardSceneSolo&kiosk',
        },
    ]

    return (
        <div className='container mx-auto px-4 py-8'>
            <h1 className='mb-6 text-2xl font-bold'>Platform Statistics</h1>

            <div className='grid grid-cols-1 gap-6 md:grid-cols-2'>
                {statsPanels.map((panel, index) => (
                    <div key={index} className='h-[250px]'>
                        <StatsPanel title={panel.title} iframeSrc={panel.iframeSrc} />
                    </div>
                ))}
            </div>
        </div>
    )
}

export default StatsPage
